/**
 * VirtualDeckGenerator.js
 * 
 * This module provides core functionality for the virtual deck randomness system.
 * It includes functions for generating secure seeds and salts, shuffling prize lists,
 * and verifying segments using cryptographic hashing.
 */

import { keccak256 } from './js-sha3';
import { generateSecureRandomString, secureRandomInt } from './SecureRandom';

/**
 * Generates a secure random seed
 * Uses the Web Crypto API for cryptographically secure randomness
 * @returns {string} A secure random seed
 */
export const generateSecureSeed = () => {
  return generateSecureRandomString(32);
};

/**
 * Generates a secure random salt
 * Uses the Web Crypto API for cryptographically secure randomness
 * @returns {string} A secure random salt
 */
export const generateSecureSalt = () => {
  return generateSecureRandomString(32);
};

/**
 * Shuffles a prize list deterministically using seed and salt
 * @param {Array} prizeList - List of prizes to shuffle
 * @param {string} seed - The random seed
 * @param {string} salt - The secret salt
 * @returns {Array} The shuffled prize list
 */
export const shufflePrizeList = (prizeList, seed, salt) => {
  const result = [];
  const remaining = [...prizeList];
  
  for (let i = 0; i < prizeList.length; i++) {
    // Create a deterministic hash for this position
    const hashInput = seed + salt + i.toString();
    const hash = keccak256(hashInput);
    
    // Convert hash to a number and use it to select a prize
    // Using the alternative approach that doesn't require BigInt
    const hashBytes = hash.match(/.{1,2}/g).map(byte => parseInt(byte, 16));
    const seedValue = hashBytes.reduce((acc, val) => acc + val, 0);
    const index = secureRandomInt(0, remaining.length - 1, seedValue);
    
    // Add the selected prize to the result and remove it from remaining
    result.push(remaining.splice(index, 1)[0]);
  }
  
  return result;
};

/**
 * Gets the prize at a specific index in the virtual deck
 * @param {Array} prizeList - Original unshuffled prize list
 * @param {string} seed - The seed used to generate the deck
 * @param {string} salt - The salt used to generate the deck
 * @param {number} index - The index of the card to get
 * @returns {Object} The prize at the specified index
 */
export const getPrizeAtIndex = (prizeList, seed, salt, index) => {
  // Shuffle the prize list deterministically
  const shuffled = shufflePrizeList(prizeList, seed, salt);
  
  // Return the prize at the specified index
  return shuffled[index];
};

/**
 * Generates a hash for a segment
 * @param {Array} prizeList - The prize list for this segment
 * @param {string} seed - The seed for this segment
 * @param {string} salt - The salt for this segment
 * @returns {string} The segment hash
 */
export const generateSegmentHash = (prizeList, seed, salt) => {
  return keccak256(seed + salt + JSON.stringify(prizeList));
};

/**
 * Verifies a segment against its hash
 * @param {Array} prizeList - The prize list for this segment
 * @param {string} seed - The revealed seed for this segment
 * @param {string} salt - The revealed salt for this segment
 * @param {string} claimedHash - The hash to verify against
 * @returns {boolean} Whether the segment is valid
 */
export const verifySegment = (prizeList, seed, salt, claimedHash) => {
  const calculatedHash = generateSegmentHash(prizeList, seed, salt);
  return calculatedHash === claimedHash;
};

/**
 * Summarizes a prize list into a distribution
 * @param {Array} prizeList - The prize list
 * @returns {Object} The prize distribution
 */
export const summarizePrizeDistribution = (prizeList) => {
  const distribution = {};
  
  prizeList.forEach(prize => {
    const key = typeof prize === 'object' ? prize.value || prize.amount : prize;
    if (!distribution[key]) {
      distribution[key] = 0;
    }
    distribution[key]++;
  });
  
  return distribution;
};
